import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, User, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { ClientesService, type Cliente, type UpdateClienteDto } from '../../services/clientes/clientes.service';
import { NotificationToast } from '../ui/NotificationToast';

const editClienteSchema = z.object({
  nombres: z.string().min(1, 'El nombre es requerido').min(2, 'Mínimo 2 caracteres'),
  apellidos: z.string().min(1, 'El apellido es requerido').min(2, 'Mínimo 2 caracteres'),
  telefono: z.string().min(1, 'El teléfono es requerido').min(7, 'Mínimo 7 dígitos'),
  email: z.string().min(1, 'El email es requerido').email('Email inválido'),
  numeroDocumento: z.string().optional(),
  direccion: z.string().optional(),
});

type EditClienteFormData = z.infer<typeof editClienteSchema>;

interface EditClienteDrawerProps {
  isOpen: boolean;
  cliente: Cliente | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditClienteDrawer: React.FC<EditClienteDrawerProps> = ({
  isOpen,
  cliente,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditClienteFormData>({
    resolver: zodResolver(editClienteSchema),
  });

  useEffect(() => {
    if (isOpen && cliente) {
      reset({
        nombres: cliente.nombres,
        apellidos: cliente.apellidos,
        telefono: cliente.telefono,
        email: cliente.email,
        numeroDocumento: cliente.numeroDocumento || '',
        direccion: cliente.direccion || '',
      });
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        reset();
      }, 300);
    }
  }, [isOpen, cliente, reset]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const onSubmit = async (data: EditClienteFormData) => {
    if (!cliente) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const updateData: UpdateClienteDto = {
        nombres: data.nombres,
        apellidos: data.apellidos,
        telefono: data.telefono,
        email: data.email,
        numeroDocumento: data.numeroDocumento || undefined,
        direccion: data.direccion || undefined,
      };

      await ClientesService.updateCliente(cliente.id, updateData);

      setNotificationConfig({
        title: '¡Cliente actualizado!',
        message: `Los datos de ${data.nombres} ${data.apellidos} fueron actualizados exitosamente.`,
        type: 'success'
      });
      setShowNotification(true);

      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1500);

    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al actualizar cliente';
      setErrorMessage(errorMsg);

      setNotificationConfig({
        title: 'Error al actualizar cliente',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !cliente) return null;

  const inputClass = (error?: any) =>
    `w-full px-4 py-3 border rounded-lg transition-all focus:ring-4 focus:outline-none ${
      error
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
        : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500/20'
    }`;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 cursor-pointer" onClick={handleClose} aria-label="Cerrar drawer" />
        <section className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="h-full flex flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Editar Cliente</h2>
              <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Info del cliente */}
              <div className="mb-6 text-center py-4 bg-gradient-to-b from-emerald-50 to-white rounded-lg">
                <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 flex items-center justify-center mb-2 shadow-lg">
                  <span className="text-white text-lg font-bold">
                    {cliente.nombres.charAt(0)}{cliente.apellidos.charAt(0)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{cliente.nombres} {cliente.apellidos}</h3>
                <p className="text-gray-600 text-sm">{cliente.email}</p>
              </div>

              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{errorMessage}</p>
                </div>
              )}

              <form id="edit-cliente-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Nombres y Apellidos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-1" />
                      Nombres *
                    </label>
                    <input type="text" {...register('nombres')} className={inputClass(errors.nombres)} />
                    {errors.nombres && <p className="mt-1 text-sm text-red-500">{errors.nombres.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-1" />
                      Apellidos *
                    </label>
                    <input type="text" {...register('apellidos')} className={inputClass(errors.apellidos)} />
                    {errors.apellidos && <p className="mt-1 text-sm text-red-500">{errors.apellidos.message}</p>}
                  </div>
                </div>

                {/* Teléfono y Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Teléfono *
                    </label>
                    <input type="tel" {...register('telefono')} className={inputClass(errors.telefono)} />
                    {errors.telefono && <p className="mt-1 text-sm text-red-500">{errors.telefono.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email *
                    </label>
                    <input type="email" {...register('email')} className={inputClass(errors.email)} />
                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
                  </div>
                </div>

                {/* Documento y Dirección */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Número de Documento
                    </label>
                    <input type="text" {...register('numeroDocumento')} className={inputClass()} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Dirección
                    </label>
                    <input type="text" {...register('direccion')} className={inputClass()} />
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="edit-cliente-form"
                  disabled={isLoading}
                  className="flex-1 bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isLoading ? 'Actualizando...' : 'Actualizar Cliente'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default EditClienteDrawer;
